import axios from '@/config/axios';
import Vue from 'vue';

// ==================== Theme Persistence Helpers ====================
const THEME_STORAGE_KEY = 'app_theme_data';

/**
 * Save theme data to localStorage
 */
function saveThemeToStorage(themeData) {
  try {
    const dataToStore = {
      currentTheme: themeData.currentTheme,
      themePalette: themeData.themePalette,
      themeType: themeData.themeType,
      themeMode: themeData.themeMode,
      textSettings: themeData.textSettings,
      timestamp: Date.now(),
    };
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(dataToStore));
  } catch (error) {
    console.warn('Failed to save theme to localStorage:', error);
  }
}

/**
 * Load theme data from localStorage
 */
function loadThemeFromStorage() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to load theme from localStorage:', error);
  }
  return null;
}

/**
 * Clear theme data from localStorage
 */
function clearThemeFromStorage() {
  try {
    localStorage.removeItem(THEME_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear theme from localStorage:', error);
  }
}

/**
 * Apply theme colors to CSS custom properties
 */
function applyThemeColors(themePalette) {
  if (!themePalette || typeof themePalette !== 'object') {
    return;
  }

  const colorMap = {
    '--color-primary-brand': '--primary-color-brand',
    '--color-brand-500': '--brand-500',
    '--color-brand-400': '--brand-400',
    '--color-brand-300': '--brand-300',
    '--color-brand-200': '--brand-200',
    '--color-brand-100': '--brand-100',
    '--color-brand-50': '--brand-50',
    '--page-background': '--page-background',
    '--container-background': '--container-background',
    '--page-border': '--page-border',
    '--card-color': '--card-color',
    '--card-border': '--card-border',
    '--filter-border': '--filter-border',
    '--color-black': '--color-black',
    '--color-white': '--color-white',
    '--color-black-950': '--black-950',
    '--color-black-900': '--black-900',
    '--color-black-800': '--black-800',
    '--color-black-700': '--black-700',
    '--color-black-600': '--black-600',
    '--color-black-500': '--black-500',
    '--color-black-400': '--black-400',
    '--color-black-300': '--black-300',
    '--color-black-200': '--black-200',
    '--color-black-150': '--black-150',
    '--color-black-100': '--black-100',
    '--color-black-50': '--black-50',
    '--color-hyperlink': '--hyper-links-blue',
    '--color-active': '--active-color',
    '--color-inactive': '--inactive-color',
    '--color-assign': '--green-assign-color',
    '--color-note': '--note-orange-color',
    '--color-ai-gradient': '--ai-button-gradient',
  };

  Object.entries(themePalette).forEach(([key, value]) => {
    const cssVar = colorMap[key] || key;
    document.documentElement.style.setProperty(cssVar, value);
  });
}

/**
 * Apply text settings to CSS custom properties
 */
function applyTextSettings(textSettings) {
  if (!textSettings || typeof textSettings !== 'object') {
    return;
  }

  if (textSettings.labelSize) {
    document.documentElement.style.setProperty('--text-LabelSize', textSettings.labelSize);
  }
  if (textSettings.dataSize) {
    document.documentElement.style.setProperty('--text-DataSize', textSettings.dataSize);
  }
  if (textSettings.labelWeight) {
    document.documentElement.style.setProperty(
      '--font-weight-LabelWeight',
      textSettings.labelWeight
    );
  }
  if (textSettings.dataWeight) {
    document.documentElement.style.setProperty('--font-weight-DataWeight', textSettings.dataWeight);
  }
  if (textSettings.labelColor) {
    document.documentElement.style.setProperty('--color-label-color', textSettings.labelColor);
  }
  if (textSettings.dataColor) {
    document.documentElement.style.setProperty('--color-data-color', textSettings.dataColor);
  }
}

/**
 * Initialize theme from localStorage on app startup
 * Call this BEFORE Vue mounts to prevent flash of default colors
 * This is exported so it can be used in main.js before Vue initializes
 */
export function initializeThemeEarly() {
  const themeData = loadThemeFromStorage();
  if (themeData) {
    if (themeData.themePalette) {
      applyThemeColors(themeData.themePalette);
    }
    if (themeData.textSettings) {
      applyTextSettings(themeData.textSettings);
    }
    return true;
  }
  return false;
}

// ==================== Vuex Store ====================

// initial state
const initialState = () => {
  return {
    currentTheme: null,
    availableThemes: [],
    theme_palatte: null,
    themeType: '',
    themeMode: '',
    textSettings: {
      labelSize: '14px',
      dataSize: '16px',
      labelWeight: '400',
      dataWeight: '400',
      labelColor: '#6D6D6D',
      dataColor: '#4F4F4F',
    },
  };
};

const state = initialState();

const mutations = {
  SET_CURRENT_THEME(state, theme) {
    state.currentTheme = theme;
  },
  SET_AVAILABLE_THEMES(state, themes) {
    state.availableThemes = themes;
  },
  SET_THEME_PALATTE(state, palette) {
    state.theme_palatte = palette;
  },
  SET_CURRENT_THEME_TYPE(state, themeType) {
    state.themeType = themeType;
  },
  SET_CURRENT_THEME_MODE(state, themeMode) {
    state.themeMode = themeMode;
  },
  SET_TEXT_SETTINGS(state, textSettings) {
    state.textSettings = textSettings;
  },
  resetData: (state) => {
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
};

const actions = {
  async updateTheme({ commit, rootState, state, dispatch }, theme) {
    try {
      const companyId = rootState.company.companyDetails?._id;
      if (!companyId) {
        throw new Error('Company ID not found');
      }

      const hasExistingTheme = state.currentTheme !== null;
      let response;
      if (hasExistingTheme) {
        response = await axios.put(`/theme/${companyId}`, {
          themePalette: theme.theme,
          themeType: theme.themeType,
          themeMode: theme.themeMode,
          textSettings: theme.textSettings,
        });
      } else {
        response = await axios.post(`/theme`, {
          companyId,
          themePalette: theme.theme,
          themeType: theme.themeType,
          themeMode: theme.themeMode,
          textSettings: theme.textSettings,
        });
      }

      // Show alert only if status is 200 or 201
      if (response && (response.status === 200 || response.status === 201)) {
        Vue.prototype.$message.success('Theme updated successfully!');
      }

      // Update local state
      commit('SET_CURRENT_THEME', theme);

      if (theme) {
        commit('SET_THEME_PALATTE', theme);

        if (theme.textSettings) {
          commit('SET_TEXT_SETTINGS', theme.textSettings);
          // Apply text settings using utility
          applyTextSettings(theme.textSettings);
        }

        // Apply the colors using utility
        if (theme.theme) {
          applyThemeColors(theme.theme);
        }

        // Save to localStorage for persistence
        saveThemeToStorage({
          currentTheme: theme,
          themePalette: theme.theme,
          themeType: theme.themeType,
          themeMode: theme.themeMode,
          textSettings: theme.textSettings,
        });
      }

      // Fetch updated themes to refresh the state
      await dispatch('fetchThemes', companyId);
      return true;
    } catch (error) {
      console.error('Failed to update theme:', error);
      Vue.prototype.$message.error('Failed to update theme. Please try again.');
      throw error;
    }
  },

  async fetchThemes({ commit, rootState }, companyId) {
    try {
      const id = rootState.company.companyDetails?._id || companyId;
      if (!id) {
        throw new Error('Company ID not found');
      }

      // Fetch default themes
      const defaultResponse = await axios.get(`/theme/${id}`);

      if (!defaultResponse.data || typeof defaultResponse.data.default !== 'object') {
        throw new Error('Invalid theme data received');
      }

      // Map and validate themes from default object
      const themes = Object.values(defaultResponse.data.default)
        .filter((theme) => theme && (theme.theme_palette || theme.theme_palatte) && theme.color)
        .map((theme) => ({
          name: theme.theme_palette || theme.theme_palatte,
          colors: theme.color,
        }));

      // Set available themes from default
      commit('SET_AVAILABLE_THEMES', themes);

      // Fetch custom theme data to get the selected theme
      try {
        const customResponse = await axios.get(`/theme/custom/${id}`);
        // Fix: Access currentTheme correctly (remove .Object)
        const currentTheme = customResponse.data?.selected_theme_palatte || null;
        commit('SET_CURRENT_THEME', currentTheme);

        // Check if this is actually a solid_gradient (saved as gradient but has page-background)
        let themeType = customResponse.data?.theme_type;
        if (themeType === 'gradient' && currentTheme?.['--page-background']) {
          // This is a solid_gradient theme
          themeType = 'solid_gradient';
        }

        commit('SET_CURRENT_THEME_TYPE', themeType);
        commit('SET_CURRENT_THEME_MODE', customResponse.data?.theme_mode);

        // Load text settings if available
        if (customResponse.data?.textSettings) {
          commit('SET_TEXT_SETTINGS', customResponse.data.textSettings);
          // Apply text settings using utility
          applyTextSettings(customResponse.data.textSettings);
        }
        if (currentTheme) {
          commit('SET_THEME_PALATTE', currentTheme);
          // Apply theme colors using utility
          applyThemeColors(currentTheme);

          // Save to localStorage for persistence
          saveThemeToStorage({
            currentTheme: currentTheme,
            themePalette: currentTheme,
            themeType: themeType,
            themeMode: customResponse.data?.theme_mode,
            textSettings: customResponse.data?.textSettings,
          });
        }
      } catch (customError) {
        console.warn('Failed to fetch custom theme data:', customError);
        // Fallback to default response
        const currentTheme = defaultResponse.data.custom?.theme || null;
        commit('SET_CURRENT_THEME', currentTheme);

        // Find and set the theme palette for fallback case
        if (currentTheme) {
          const matchingTheme = themes.find((theme) => theme.name === currentTheme);
          if (matchingTheme && matchingTheme.colors) {
            commit('SET_THEME_PALATTE', matchingTheme.colors);
            // Apply theme colors using utility
            applyThemeColors(matchingTheme.colors);
          }
        }
      }

      return themes;
    } catch (error) {
      console.error('Failed to fetch themes:', error);
      Vue.prototype.$message.error('Failed to fetch themes. Please refresh or contact support.');
      commit('SET_AVAILABLE_THEMES', []);
      commit('SET_CURRENT_THEME', null);
      const defaultColors = {
        '--color-primary-brand': '#178a7c',
        '--color-brand-500': '#21ab98',
        '--color-brand-400': '#3ac7b1',
        '--color-brand-300': '#69dfca',
        '--color-brand-200': '#a0efde',
        '--color-brand-100': '#d0f7ee',
        '--color-brand-50': '#f1fcf9',
        '--color-black-900': '#2F80ED',
        '--color-black-800': '#2F80ED',
        '--color-black-700': '#2F80ED',
        '--color-black-600': '#2F80ED',
        '--color-black-500': '#2F80ED',
        '--color-black-400': '#2F80ED',
        '--color-black-300': '#2F80ED',
        '--color-black-200': '#2F80ED',
        '--color-black-150': '#2F80ED',
        '--color-black-100': '#2F80ED',
        '--color-black-50': '#2F80ED',
        '--color-hyperlink': '#2F80ED',
        '--color-active': '#2F80ED',
        '--color-inactive': '#2F80ED',
        '--color-assign': '#2F80ED',
        '--color-note': '#2F80ED',
      };
      // Apply default colors using utility
      applyThemeColors(defaultColors);
      throw error;
    }
  },

  // Action to manually apply text settings to CSS variables
  applyTextSettingsToCSS({ state }) {
    if (state.textSettings) {
      applyTextSettings(state.textSettings);
    }
  },

  // Action to completely clear theme state on logout
  resetTheme({ commit }) {
    // Clear theme from localStorage FIRST
    clearThemeFromStorage();

    // Reset state to initial/empty values using the resetData mutation
    commit('resetData');

    // Remove all theme-related CSS variables from the document
    const cssVarsToRemove = [
      '--primary-color-brand',
      '--brand-500',
      '--brand-400',
      '--brand-300',
      '--brand-200',
      '--brand-100',
      '--brand-50',
      '--page-background',
      '--container-background',
      '--page-border',
      '--card-color',
      '--card-border',
      '--filter-border',
      '--color-black',
      '--color-white',
      '--black-950',
      '--black-900',
      '--black-800',
      '--black-700',
      '--black-600',
      '--black-500',
      '--black-400',
      '--black-300',
      '--black-200',
      '--black-150',
      '--black-100',
      '--black-50',
      '--hyper-links-blue',
      '--active-color',
      '--inactive-color',
      '--green-assign-color',
      '--note-orange-color',
      '--ai-button-gradient',
      '--text-LabelSize',
      '--text-DataSize',
      '--font-weight-LabelWeight',
      '--font-weight-DataWeight',
      '--color-label-color',
      '--color-data-color',
      '--color-primary-brand',
      '--color-brand-500',
      '--color-brand-400',
      '--color-brand-300',
      '--color-brand-200',
      '--color-brand-100',
      '--color-brand-50',
      '--color-black-950',
      '--color-black-900',
      '--color-black-800',
      '--color-black-700',
      '--color-black-600',
      '--color-black-500',
      '--color-black-400',
      '--color-black-300',
      '--color-black-200',
      '--color-black-150',
      '--color-black-100',
      '--color-black-50',
      '--color-hyperlink',
      '--color-active',
      '--color-inactive',
      '--color-assign',
      '--color-note',
      '--color-ai-gradient',
      '--colorblack',
    ];

    // Remove each CSS variable
    cssVarsToRemove.forEach((cssVar) => {
      document.documentElement.style.removeProperty(cssVar);
    });

    // The theme will be completely cleared and reloaded from the database on next login
  },
};

const getters = {
  getCurrentTheme: (state) => state.currentTheme,
  getAvailableThemes: (state) => state.availableThemes || [],
  getThemePalatte: (state) => state.theme_palatte,
  getThemeType: (state) => state.themeType,
  getThemeMode: (state) => state.themeMode,
  getTextSettings: (state) => state.textSettings,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
